/**
 * Scratch Unleashed — Lightweight Web Analytics Tracker
 * ──────────────────────────────────────────────────────────────────────────
 * Self-hosted; no external SDKs. Posts to /api/analytics/ping every 15s and
 * on every SPA route change.
 *
 * Storage
 *   • localStorage 'su_analytics_sid'  — UUIDv4 session id (TTL via inactivity)
 *   • sessionStorage 'su_analytics_last_path' — debounces redundant pings
 *
 * Behavior
 *   • Generates session UUID on first call; reuses for life of the tab/window.
 *   • Sends a ping immediately on first load.
 *   • Sends a ping on every route change (use `notifyRoute` hook).
 *   • Sends a ping every 15 s (heartbeat) ONLY while the tab is visible.
 *   • Pauses heartbeat when document.hidden is true; resumes on visibility.
 *   • On `pagehide` / `beforeunload` sends one final ping via sendBeacon.
 *   • All network errors are swallowed — analytics MUST NEVER break the UI.
 *
 * Privacy
 *   • Reads ?ref=… (if present) and forwards it to the backend so promoter
 *     attribution chains through analytics too — but doesn't store any PII.
 *   • All other PII handling (IP hashing, etc.) is done server-side.
 */

const SID_KEY        = 'su_analytics_sid';
const LAST_PATH_KEY  = 'su_analytics_last_path';
const PING_INTERVAL  = 15_000;       // 15 s
const ENDPOINT       = '/api/analytics/ping';

let timerId       = null;
let started       = false;
let backendOrigin = '';

// ── helpers ─────────────────────────────────────────────────────────────────
function uuidv4() {
  // Browser-native if available
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback (RFC4122-ish; not cryptographically strong but fine for IDs)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getOrCreateSessionId() {
  try {
    let sid = localStorage.getItem(SID_KEY);
    if (!sid || sid.length < 8) {
      sid = uuidv4();
      localStorage.setItem(SID_KEY, sid);
    }
    return sid;
  } catch (_e) {
    // Private mode / storage disabled — generate per-page session.
    return uuidv4();
  }
}

function currentPath() {
  if (typeof window === 'undefined') return '/';
  return (window.location.pathname || '/') + (window.location.search || '');
}

function refFromUrl() {
  try {
    const u = new URL(window.location.href);
    return u.searchParams.get('ref') || null;
  } catch (_e) {
    return null;
  }
}

function buildPayload() {
  const tz =
    Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || '';
  return {
    session_id:    getOrCreateSessionId(),
    current_path:  currentPath(),
    screen_width:  window.innerWidth || 0,
    screen_height: window.innerHeight || 0,
    timezone:      tz,
    referrer:      document.referrer || '',
    promoter_code: refFromUrl(),
  };
}

// ── transport ───────────────────────────────────────────────────────────────
async function sendPing({ useBeacon = false } = {}) {
  if (typeof window === 'undefined') return;
  if (document.visibilityState === 'hidden' && !useBeacon) return;

  const url = backendOrigin + ENDPOINT;
  const payload = buildPayload();
  const body = JSON.stringify(payload);

  // Last ping on unload uses sendBeacon (fire-and-forget, doesn't block)
  if (useBeacon && navigator.sendBeacon) {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } catch (_e) {
      // ignore
    }
    return;
  }

  // Normal ping: fetch with no-error UX
  try {
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      credentials: 'include',
      keepalive: true,
    });
  } catch (_e) {
    // Silent — analytics must never break the app
  }
}

// ── lifecycle hooks ─────────────────────────────────────────────────────────
function onVisibilityChange() {
  if (document.visibilityState === 'visible') {
    sendPing();
    restartHeartbeat();
  } else {
    stopHeartbeat();
  }
}

function onPageHide() {
  sendPing({ useBeacon: true });
}

function restartHeartbeat() {
  stopHeartbeat();
  timerId = setInterval(() => sendPing(), PING_INTERVAL);
}
function stopHeartbeat() {
  if (timerId) {
    clearInterval(timerId);
    timerId = null;
  }
}

// ── public API ──────────────────────────────────────────────────────────────
/**
 * Start tracking. Idempotent. Call once at app boot.
 * @param {{ backendUrl?: string }} opts
 */
export function initAnalytics({ backendUrl = '' } = {}) {
  if (started || typeof window === 'undefined') return;
  started = true;
  backendOrigin = backendUrl.replace(/\/+$/, '') || '';

  // First-load ping
  try {
    sessionStorage.setItem(LAST_PATH_KEY, currentPath());
  } catch (_e) { /* ignore */ }
  sendPing();

  // Visibility + unload hooks
  document.addEventListener('visibilitychange', onVisibilityChange, { passive: true });
  window.addEventListener('pagehide',  onPageHide, { passive: true });
  window.addEventListener('beforeunload', onPageHide, { passive: true });

  // Heartbeat
  restartHeartbeat();
}

/**
 * Notify the tracker that the SPA route just changed.
 * Call from a Router-level `useEffect(() => …, [location.pathname])`.
 */
export function notifyRoute() {
  try {
    const last = sessionStorage.getItem(LAST_PATH_KEY);
    const now  = currentPath();
    if (last !== now) {
      sessionStorage.setItem(LAST_PATH_KEY, now);
      sendPing();   // immediate ping on route change
    }
  } catch (_e) {
    sendPing();
  }
}

export function _debugCurrentSession() {
  return {
    session_id: getOrCreateSessionId(),
    path: currentPath(),
    started,
  };
}
